/**
 * utils/payments.js
 * Pure payment-row validation (Phase T2a, PRD §7.2/§8 invariants 15-16).
 *
 * Separate from txnStatus.js on purpose: that module derives total/sisa/status FROM an
 * already-persisted transaction (recomputed on demand, never stored); this module validates
 * a DRAFT array of payment rows against an intended creation-time status BEFORE anything is
 * ever persisted — input validation, not derived computation.
 *
 * Wired into App.js's createTransaction since Phase T2b: TransactionFormModal sends a real
 * multi-method `draft.payments` array, validated here before anything is written.
 *
 * Phase T3 (Bayar Tagihan): `validatePaymentSet` (below) is the intent-free half of
 * `validatePayments`, reused by App.js's `recordPayment` and (payment-correction rework)
 * `editTransaction`'s own payments guard when no intent is supplied — post-creation payment
 * writes have no Lunas/Belum-Lunas intent to satisfy unless the caller explicitly provides
 * one, only the sum<=total rule.
 *
 * Fix #6: `validatePaymentSet`'s own per-row loop also rejects a row whose `amount` isn't a
 * real, positive number — this is the ONE place both `TransactionFormModal.js` (via
 * `validatePayments`, which calls this) and `BayarTagihanModal.js` (directly) funnel through,
 * and it's also what every App.js write path already calls server-side, so this one change
 * closes the "a blank/Rp 0 payment row can be saved" gap everywhere at once.
 */

export const PAYMENT_METHODS = ["TUNAI", "TRANSFER", "CEK", "LAINNYA"];

// Shared Indonesian display labels — used by both TransactionFormModal's method dropdown and
// TransactionDetailModal's payment list, so the two surfaces can never disagree on wording.
export const METHOD_LABEL = { TUNAI: "Tunai", TRANSFER: "Bank Transfer", CEK: "Cek", LAINNYA: "Lainnya" };

/**
 * Validate a set of payment rows against a total, with NO creation-time-intent rule applied —
 * just per-row shape (method validity, methodNote required only for LAINNYA) and the one rule
 * that always holds regardless of when a payment is recorded: the sum can never exceed `total`
 * (invariant 16). Factored out of `validatePayments` (below) so post-creation write paths
 * (`App.js`'s `recordPayment`, Bayar Tagihan's add-only flow; `editTransaction`'s own payments
 * guard when no intent is supplied) can validate the FULL resulting payments array without
 * needing a Lunas/Belum-Lunas intent to satisfy.
 *
 * @returns {{ ok: boolean, error: string|null }}
 */
export function validatePaymentSet(payments, total) {
  for (const p of payments) {
    if (!p.method) {
      return { ok: false, error: "pilih metode untuk setiap baris pembayaran" };
    }
    if (!PAYMENT_METHODS.includes(p.method)) {
      return { ok: false, error: `metode pembayaran tidak valid: ${p.method}` };
    }
    if (p.method === "LAINNYA") {
      if (!p.methodNote || !p.methodNote.trim()) {
        return { ok: false, error: "metode LAINNYA wajib diisi keterangan" };
      }
    } else if (p.methodNote !== null) {
      return { ok: false, error: "methodNote harus null kecuali metode LAINNYA" };
    }
    // fix #6: a row with no real, positive amount (blank/null/0/negative) may never be saved —
    // `Number(...)` so an un-coerced null/undefined amount (both real states a fresh, untouched
    // row can be in) fails this the same way an explicit 0 does.
    if (!(Number(p.amount) > 0)) {
      return { ok: false, error: "setiap baris pembayaran harus memiliki jumlah lebih dari 0" };
    }
  }

  const sum = payments.reduce((s, p) => s + p.amount, 0);
  if (sum > total) {
    return { ok: false, error: "jumlah pembayaran melebihi total" };
  }

  return { ok: true, error: null };
}

/**
 * Validate a draft's payment rows against its total and creation-time intent.
 * `intent` is "LUNAS" | "BELUM_LUNAS" — the two creation-time states from PRD §7.2, NOT
 * `txnStatus()`'s derived LUNAS/PIUTANG/UTANG value (a different, already-existing concept).
 *
 * @returns {{ ok: boolean, error: string|null }}
 */
export function validatePayments(payments, total, intent) {
  const base = validatePaymentSet(payments, total);
  if (!base.ok) return base;

  const sum = payments.reduce((s, p) => s + p.amount, 0);
  if (intent === "LUNAS" && sum !== total) {
    return { ok: false, error: "Lunas harus berjumlah tepat sama dengan total" };
  }
  if (intent === "BELUM_LUNAS" && sum >= total) {
    return { ok: false, error: "Belum Lunas harus berjumlah kurang dari total" };
  }

  return { ok: true, error: null };
}

/**
 * Duplicate-payment prevention (paidAt duplicate-prevention feature): two payment rows are an
 * exact duplicate when their `method` AND `paidAt` (a combined ISO date+time string, minute
 * precision — `utils/idGenerators.js`'s `combineDateTime` always zeroes seconds/ms) are BOTH
 * identical. `amount` is deliberately excluded from the comparison — even differing amounts
 * don't exempt an otherwise-identical row. Two rows differing in EITHER date or time are a
 * legitimate scenario (e.g. two Tunai payments recorded weeks apart) and must never be blocked.
 *
 * This is a client-side inline-validation concern, not a money-correctness invariant — like
 * `TransactionFormModal.js`'s own `hasDuplicateNewMethod`, no matching guard is added to any
 * `App.js` write path; the check exists to keep payment records humanly distinguishable, not to
 * protect a sum/total rule `validatePaymentSet`/`validatePayments` already cover.
 *
 * Callers hold `paidAt` in different shapes: an already-persisted row carries a real `paidAt`
 * string directly; a row still being edited (`TransactionFormModal.js`/`BayarTagihanModal.js`)
 * holds split `paidAtDate`/`paidAtTime` state and must combine them via `combineDateTime` first
 * — the same call `buildDraft`/`onRecordPayment` make at submit time, so the check always
 * compares the exact value that would actually be persisted.
 */
export function isDuplicatePayment(a, b) {
  // A row with no method picked yet can't be a duplicate of anything (silent-defaults fix).
  return !!a.method && a.method === b.method && a.paidAt === b.paidAt;
}

/** Whether `candidate` ({method, paidAt}) collides (see `isDuplicatePayment`) with any row in `others`. */
export function hasDuplicatePaymentAgainst(candidate, others) {
  return others.some((o) => isDuplicatePayment(o, candidate));
}

/**
 * The single shared display text for a payment row (Phase T2b-fix) — everywhere a payment's
 * method is shown to a user, this is the ONE function that decides what it reads. A LAINNYA
 * row shows just its own note (the note IS the description the user typed — "Lainnya — Alipay"
 * is redundant); every other method shows its plain `METHOD_LABEL`. Used by
 * TransactionDetailModal's payment list, EditLogTimeline's creation-entry payment line, and
 * (later) the print templates — do not reimplement this logic a second time anywhere.
 */
export function paymentMethodText(p) {
  return p.method === "LAINNYA" ? p.methodNote : METHOD_LABEL[p.method];
}
